import { Suspense } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Routing
import { Routes, Navigate, Route } from "react-router-dom";
// Components
import { Main } from "./components";
import {
  MainPage,
  PrivacyPolicyPage,
  TermsOfUsePage,
  TherapistFormPage,
  ThankYouPage,
  JoinOurNetworkPage,
  ContactUsPage,
} from "./pages";
import { SchoolRoot } from "pages/SchoolPage/SchoolRoot";
import { TalkToAdvisor } from "pages/SchoolPage/TalkToAdvisor/TalkToAdvisor";
import { FormSuccessPage } from "pages/SchoolPage/FormSuccessPage/FormSuccessPage";
import { legacyRedirects, routes } from "constants/index";
import { RedirectWithSearch } from "components/shared/RedirectWithSearch";
import { Container } from "components/Core";

import { CircularProgress, IconButton } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Courses } from "pages/Courses/Courses";
import { CourseDetail } from "pages/Courses/CourseDetail";
import DeleteMePage from "pages/DeleteMePage";
import NumuwLinking from "pages/NumuwLinking";
import SupportRequestPage from "pages/SupportRequestPage";
import ServiceAgreementPage from "pages/ServiceAgreementPage";
import { fetchSupportContacts } from "utils/remoteConfig";
import { detectDevice, launchAppOrFallback } from "utils";

const IOS_WHATSAPP_STORE_URL =
  "https://apps.apple.com/app/whatsapp-messenger/id310633997";
const ANDROID_WHATSAPP_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.whatsapp";
// App
const App = () => {
  // Intercom launcher disabled in favor of WhatsApp.
  // Intercom({
  //   app_id: "h2boatyn",
  // });

  const { supportPhoneNumber } = fetchSupportContacts();
  const whatsappPhone = (supportPhoneNumber || "").replace(/\D/g, "");
  const { platform } = detectDevice();
  const isMobileDevice =
    typeof window !== "undefined" &&
    /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
  const whatsappDeepLink = whatsappPhone
    ? `whatsapp://send?phone=${whatsappPhone}`
    : "whatsapp://send";
  const whatsappLink = whatsappPhone
    ? isMobileDevice
      ? `https://wa.me/${whatsappPhone}`
      : `https://web.whatsapp.com/send?phone=${whatsappPhone}`
    : `https://wa.me/${whatsappPhone}/`;

  const handleWhatsAppClick = (event) => {
    if (platform !== "android" && platform !== "ios") {
      return;
    }

    event.preventDefault();

    launchAppOrFallback({
      deepLink: whatsappDeepLink,
      fallbackUrl:
        platform === "android"
          ? ANDROID_WHATSAPP_STORE_URL
          : IOS_WHATSAPP_STORE_URL,
    });
  };

  return (
    <>
      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
      />
      <Suspense
        fallback={
          <Container height={["100vh"]}>
            <CircularProgress />
          </Container>
        }
      >
        <Routes>
          <Route
            path="/"
            element={<Main header={true} consultButton={true} footer={true} />}
          >
            <Route index element={<MainPage />} />
          </Route>

          <Route
            path="/join-us"
            element={<Main header={true} consultButton={true} footer={false} />}
          >
            <Route index element={<TherapistFormPage />} />
          </Route>

          <Route
            path="/privacy-policy"
            element={
              <Main header={false} consultButton={true} footer={false} />
            }
          >
            <Route index element={<PrivacyPolicyPage />} />
          </Route>

          <Route
            path="/terms-of-use"
            element={
              <Main header={false} consultButton={true} footer={false} />
            }
          >
            <Route index element={<TermsOfUsePage />} />
          </Route>

          <Route
            path="/service-agreement"
            element={
              <Main header={false} consultButton={true} footer={false} />
            }
          >
            <Route index element={<ServiceAgreementPage />} />
          </Route>

          <Route
            path="/thank-you"
            element={<Main header={true} consultButton={true} footer={true} />}
          >
            <Route index element={<ThankYouPage />} />
          </Route>

          <Route
            path="/join-our-network"
            element={<Main header={true} consultButton={false} footer={true} />}
          >
            <Route index element={<JoinOurNetworkPage />} />
          </Route>

          <Route
            path="/contact-us"
            element={<Main header={true} consultButton={true} footer={true} />}
          >
            <Route index element={<ContactUsPage />} />
          </Route>
          <Route
            path="/deleteme"
            element={<Main header={true} consultButton={false} footer={true} />}
          >
            <Route index element={<DeleteMePage />} />
          </Route>

          <Route
            path="/support-request"
            element={<Main header={true} consultButton={false} footer={true} />}
          >
            <Route index element={<SupportRequestPage />} />
          </Route>

          <Route path="/deeplink/*" element={<NumuwLinking />} />
          <Route
            path="/courses"
            element={<Main header={true} consultButton={false} footer={true} />}
          >
            <Route index element={<Courses />} />
          </Route>
          <Route
            path="/course/:courseId"
            element={<Main header={true} consultButton={false} footer={true} />}
          >
            <Route index element={<CourseDetail to="/" />} />
          </Route>
          <Route
            path={routes.school}
            element={
              <Main
                hideGetStarted
                header
                consultButton={false}
                footer
                hideSchoolNav
                languageBlockDisplayMediaQuery={["none", "none", "block"]}
                // hideSignIn
              />
            }
          >
            <Route index element={<SchoolRoot />} />
          </Route>
          <Route
            path={routes.schoolPartnership}
            element={
              <Main
                navigateTo={routes.school}
                header={true}
                consultButton={false}
                footer={true}
                hideGetStarted
                hideSchoolNav
              />
            }
          >
            <Route index element={<TalkToAdvisor />} />
          </Route>
          <Route
            path={routes.schoolPartnershipSuccess}
            element={
              <Main
                navigateTo={routes.school}
                header={true}
                consultButton={false}
                hideGetStarted
                hideSchoolNav
              />
            }
          >
            <Route index element={<FormSuccessPage />} />
          </Route>
          {legacyRedirects.map(({ from, to }) => (
            <Route
              key={from}
              path={from}
              element={<RedirectWithSearch to={to} />}
            />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <IconButton
        component="a"
        href={whatsappLink}
        onClick={handleWhatsAppClick}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: "999px",
          backgroundColor: "#25D366",
          color: "#ffffff",
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
          zIndex: 1000,
          "&:hover": {
            backgroundColor: "#20ba5a",
          },
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 28 }} />
      </IconButton>
    </>
  );
};

export default App;
